import { useEffect, useState } from 'react'
import { Button, CircularProgress } from '@mui/material'

import { SystemPrompt } from '../../api/models'

import SystemPromptEditor from './SystemPromptEditor'
import { useSystemPrompts } from './useSystemPrompts'

import styles from './SystemPrompts.module.css'

export default function SystemPrompts() {
  const { prompts, isLoading, hasError, refetch, updateLocal } = useSystemPrompts()
  const [selectedKey, setSelectedKey] = useState<string | null>(null)

  useEffect(() => {
    if (!selectedKey && prompts.length > 0) {
      setSelectedKey(prompts[0].key)
    }
  }, [prompts, selectedKey])

  const selectedPrompt = prompts.find(p => p.key === selectedKey) ?? null

  const handleMutation = (prompt: SystemPrompt) => {
    updateLocal(prompt)
  }

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <h1 className={styles.headerText}>System Prompts</h1>
        <span className={styles.headerDescription}>
          Review and edit the system prompts used by the AI. Changes take effect on the next AI run.
        </span>
      </div>

      {isLoading && (
        <div className={styles.stateContainer}>
          <CircularProgress />
        </div>
      )}

      {!isLoading && hasError && (
        <div className={styles.stateContainer}>
          <span className={styles.errorText}>There was an issue loading system prompts. Please try again later.</span>
          <Button variant="outlined" onClick={refetch}>
            Retry
          </Button>
        </div>
      )}

      {!isLoading && !hasError && prompts.length === 0 && (
        <div className={styles.stateContainer}>
          <span>No system prompts found.</span>
        </div>
      )}

      {!isLoading && !hasError && prompts.length > 0 && (
        <div className={styles.masterDetail}>
          <div className={styles.list}>
            {prompts.map(prompt => (
              <div
                key={prompt.key}
                className={prompt.key === selectedKey ? styles.listItemSelected : styles.listItem}
                onClick={() => setSelectedKey(prompt.key)}>
                <span className={styles.listItemName}>{prompt.name}</span>
                <span className={styles.listItemDescription}>{prompt.description}</span>
                <span className={styles.listItemBadge}>{prompt.is_custom ? 'Custom' : 'Default'}</span>
              </div>
            ))}
          </div>

          <div className={styles.detail}>
            {selectedPrompt && (
              <SystemPromptEditor
                key={selectedPrompt.key}
                prompt={selectedPrompt}
                onSaved={handleMutation}
                onReset={handleMutation}
              />
            )}
          </div>
        </div>
      )}
    </div>
  )
}
